/**
 * @module angi-bridge
 * @description Bridges the Angi assembly upsell widget with the native cart
 * drawer. Angi dispatches a `cart:added` CustomEvent after a successful add
 * but never opens our drawer. This module listens for that event and fires
 * the standard cart-drawer open + refresh sequence, including a cart.js fetch
 * so header badge counts stay in sync — mirroring AddToCart.#syncCart.
 *
 * Also injects the Angi upsell widget's visibility rule (see below). All Angi
 * coupling lives here; the rest of the theme stays vendor-neutral.
 *
 * This listener depends on Angi dispatching `cart:added` with
 * `detail.source === 'angi-product-upsell'`. If the upstream integration
 * changes the event name or payload shape, this must be updated to match.
 */

import { CART_EVENTS } from '@stardust/cart';
import { getCart } from '@stardust/cart-api';

const CART_DRAWER_OPEN = 'cart-drawer:open';

// Hide the Angi upsell widget when the buy box isn't purchasable (pre-order or
// out-of-stock). Reads the generic `data-button-type` the buy box already
// publishes — product.js keeps it current on variant change — and stays
// reactive via CSS :has() so there's no flash while the third-party widget
// mounts. `.angi-upsell-widget` is markup owned by the external Angi
// integration, not the theme; if Angi changes that class/mount point, update here.
const ANGI_VISIBILITY_STYLE_ID = 'angi-upsell-visibility';
if (!document.getElementById(ANGI_VISIBILITY_STYLE_ID)) {
  const style = document.createElement('style');
  style.id = ANGI_VISIBILITY_STYLE_ID;
  style.textContent = `
    [product-detail]:has([data-button-type="pre-order"]) .angi-upsell-widget,
    [product-detail]:has([data-button-type="out-of-stock"]) .angi-upsell-widget {
      display: none !important;
    }
  `;
  document.head.appendChild(style);
}

document.addEventListener('cart:added', async (e) => {
    if (e.detail?.source !== 'angi-product-upsell') return;

    document.dispatchEvent(new CustomEvent(CART_DRAWER_OPEN, {
      bubbles: true,
      detail: { fromAddToCart: true, ariaLiveText: 'Assembly service added to cart' },
    }));

    // Fetch full cart so CART_EVENTS.updated fires and header badge counts update.
    // Mirrors AddToCart.#syncCart — Angi's cartData is items-only, not a full cart.
    let cartData = null;
    try {
      cartData = await getCart();
      document.dispatchEvent(new CustomEvent(CART_EVENTS.updated, { detail: cartData }));
    } catch (err) {
      console.error('[angi-bridge] Failed to fetch cart.js:', err);
    }

    document.dispatchEvent(new CustomEvent(CART_EVENTS.refreshDrawer, { detail: cartData }));
});
